import { ReactFramework } from "@/utils/templateProjects";
import { useActiveProject } from "@/utils/utils";
import {
  Columns2,
  FrameIcon,
  GlobeIcon,
  Grid2X2,
  LucideIcon,
  Rows2,
} from "lucide-react";

export enum LayoutId {
  APPLICATION = "application",
  BLANK = "blank",
  HORIZONTAL_LAYOUT = "horizontal-layout",
  VERTICAL_LAYOUT = "vertical-layout",
  GRID_LAYOUT = "grid-layout",
}

export type LayoutPreset = {
  id: LayoutId;
  name: string;
  jsx: string;
  htmlInstantUpdate: string;
  getJsxWithCustomClasses: (customClasses: string) => string;
  getHtmlWithCustomStyles: (extraStyles: string) => string;
  icon: LucideIcon;
};

const options: LayoutPreset[] = [
  {
    id: LayoutId.BLANK,
    name: "Blank",
    jsx: `<div className="w-full h-full"></div>`,
    htmlInstantUpdate: `<div className="w-full h-full"></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<div className="${customClasses}"></div>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="${customStyles}"></div>`,
    icon: FrameIcon,
  },
  {
    id: LayoutId.HORIZONTAL_LAYOUT,
    name: "Horizontal Layout",
    jsx: `<div className="flex flex-row space-x-1"><div className="w-32 bg-blue-100">01</div><div className="w-32 bg-blue-200">02</div></div>`,
    htmlInstantUpdate: `<div style="display: flex;margin-left: 0.25rem;flex-direction: row;"><div style="width: 8rem;background-color: #DBEAFE;">01</div><div style="width: 8rem;background-color: #BFDBFE;">02</div></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<div className="flex flex-row space-x-1 ${customClasses}"><div className="w-32 bg-blue-100">01</div><div className="w-32 bg-blue-200">02</div></div>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="display: flex;margin-left: 0.25rem;flex-direction: row;${customStyles}"><div style="width: 8rem;background-color: #DBEAFE;">01</div><div style="width: 8rem;background-color: #BFDBFE;">02</div></div>`,
    icon: Columns2,
  },
  {
    id: LayoutId.VERTICAL_LAYOUT,
    name: "Vertical Layout",
    jsx: `<div className="flex flex-col space-y-1"><div className="h-32 bg-blue-100">01</div><div className="h-32 bg-blue-200">02</div></div>`,
    htmlInstantUpdate: `<div style="display: flex;margin-top: 0.25rem;flex-direction: column;"><div style="height: 8rem;background-color: #DBEAFE;">01</div><div style="height: 8rem;background-color: #BFDBFE;">02</div></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<div className="flex flex-col space-y-1 ${customClasses}"><div className="h-32 bg-blue-100">01</div><div className="h-32 bg-blue-200">02</div></div>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="display: flex;margin-top: 0.25rem;flex-direction: column;${customStyles}"><div style="height: 8rem;background-color: #DBEAFE;">01</div><div style="height: 8rem;background-color: #BFDBFE;">02</div></div>`,
    icon: Rows2,
  },
  {
    id: LayoutId.GRID_LAYOUT,
    name: "Grid Layout",
    jsx: `<div className="grid grid-cols-2 grid-rows-2 gap-1"><div className="bg-blue-100">01</div><div className="bg-blue-200">02</div><div className="bg-blue-300">03</div><div className="bg-blue-400">04</div></div>`,
    htmlInstantUpdate: `<div style="display: grid grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;"><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<div className="grid grid-cols-2 grid-rows-2 gap-1 ${customClasses}"><div className="bg-blue-100">01</div><div className="bg-blue-200">02</div><div className="bg-blue-300">03</div><div className="bg-blue-400">04</div></div>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="display: grid grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;${customStyles}"><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`,
    icon: Grid2X2,
  },
  {
    id: LayoutId.APPLICATION,
    name: "Route",
    jsx: ``,
    htmlInstantUpdate: ``,
    getJsxWithCustomClasses: (customClasses: string) => ``,
    getHtmlWithCustomStyles: (customStyles: string) => ``,
    icon: GlobeIcon,
  },
];

const expoOptions: LayoutPreset[] = [
  {
    id: LayoutId.BLANK,
    name: "Blank",
    jsx: `<View style={{width: '100%', height: '100%'}}></View>`,
    htmlInstantUpdate: `<div className="w-full h-full"></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<View style={${customClasses}}></View>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="${customStyles}"></div>`,
    icon: FrameIcon,
  },
  {
    id: LayoutId.HORIZONTAL_LAYOUT,
    name: "Horizontal Layout",
    jsx: `<View style={{display: 'flex', flexDirection: 'row', columnGap: 4}}><View style={{width: 128, backgroundColor: '#DBEAFE'}}>01</View><View style={{width: 128, backgroundColor: '#BFDBFE'}}>02</View></View>`,
    htmlInstantUpdate: `<div style="display: flex;margin-left: 0.25rem;flex-direction: row;"><div style="width: 8rem;background-color: #DBEAFE;">01</div><div style="width: 8rem;background-color: #BFDBFE;">02</div></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<View style={{display: 'flex', flexDirection: 'row', columnGap: 4, ...${customClasses}}}><View style={{width: 128, backgroundColor: '#DBEAFE'}}>01</View><View style={{width: 128, backgroundColor: '#BFDBFE'}}>02</View></View>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="display: flex;margin-left: 0.25rem;flex-direction: row;${customStyles}"><div style="width: 8rem;background-color: #DBEAFE;">01</div><div style="width: 8rem;background-color: #BFDBFE;">02</div></div>`,
    icon: Columns2,
  },
  {
    id: LayoutId.VERTICAL_LAYOUT,
    name: "Vertical Layout",
    jsx: `<View style={{display: 'flex', flexDirection: 'column', rowGap: 4}}><View style={{height: 128, backgroundColor: '#DBEAFE'}}>01</View><View style={{height: 128, backgroundColor: '#BFDBFE'}}>02</View></View>`,
    htmlInstantUpdate: `<div style="display: flex;margin-top: 0.25rem;flex-direction: column;"><div style="height: 8rem;background-color: #DBEAFE;">01</div><div style="height: 8rem;background-color: #BFDBFE;">02</div></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<View style={{display: 'flex', flexDirection: 'column', rowGap: 4, ...${customClasses}}}><View style={{height: 128, backgroundColor: '#DBEAFE'}}>01</View><View style={{height: 128, backgroundColor: '#BFDBFE'}}>02</View></View>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="display: flex;margin-top: 0.25rem;flex-direction: column;${customStyles}"><div style="height: 8rem;background-color: #DBEAFE;">01</div><div style="height: 8rem;background-color: #BFDBFE;">02</div></div>`,
    icon: Rows2,
  },
  {
    id: LayoutId.GRID_LAYOUT,
    name: "Grid Layout",
    jsx: `<View style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: 4}}><View style={{backgroundColor: '#DBEAFE'}}>01</View><View style={{backgroundColor: '#BFDBFE'}}>02</View><View style={{backgroundColor: '#93C5FD'}}>03</View><View style={{backgroundColor: '#60A5FA'}}>04</View></View>`,
    htmlInstantUpdate: `<div style="display: grid grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;"><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`,
    getJsxWithCustomClasses: (customClasses: string) =>
      `<View style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: 4, ...${customClasses}}}><View style={{backgroundColor: '#DBEAFE'}}>01</View><View style={{backgroundColor: '#BFDBFE'}}>02</View><View style={{backgroundColor: '#93C5FD'}}>03</View><View style={{backgroundColor: '#60A5FA'}}>04</View></View>`,
    getHtmlWithCustomStyles: (customStyles: string) =>
      `<div style="display: grid grid-template-columns: repeat(2, minmax(0, 1fr));grid-template-rows: repeat(2, minmax(0, 1fr));gap: 0.25rem;${customStyles}"><div style="background-color: #DBEAFE;">01</div><div style="background-color: #BFDBFE;">02</div><div style="background-color: #93C5FD;">03</div><div style="background-color: #60A5FA;">04</div></div>`,
    icon: Grid2X2,
  },
  {
    id: LayoutId.APPLICATION,
    name: "Route",
    jsx: ``,
    htmlInstantUpdate: ``,
    getJsxWithCustomClasses: (customClasses: string) => ``,
    getHtmlWithCustomStyles: (customStyles: string) => ``,
    icon: GlobeIcon,
  },
];

const useLayoutPresets = () => {
  const project = useActiveProject();

  return {
    options: project?.framework === ReactFramework.EXPO ? expoOptions : options,
  };
};

export default useLayoutPresets;
